import { useNavigate } from 'react-router';
import { Column, ColumnDef, FilterFn, Row } from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import { Button } from '@curtin-dxp/curtin-ui';
import { DataTableColumnHeader } from '@/components/Common/DataTable';
import { MappedSchoolUnitDetails } from '#server/models';

const createHeader = <TData,>(title: string, sortable: boolean) => {
  return ({ column }: { column: Column<TData, unknown> }) => (
    <DataTableColumnHeader
      column={column}
      title={title}
      sortable={sortable}
      titleClassName="text-secondary-grey-02 pl-0 pr-8"
    />
  );
};

const createCell =
  (key: string, className?: string) =>
  ({ row }: { row: Row<MappedSchoolUnitDetails> }) => {
    const navigate = useNavigate();
    let value;

    const handleClick = () => {
      const unitWorkloadId = row.original['unitWorkloadId'];
      navigate(`/unit-workloads/${unitWorkloadId}`);
    };
    switch (key) {
      case 'unit':
        value = (
          <>
            <span className="text-b2 font-semibold text-primary-black-90">
              <a
                href={`/unit-workloads/${row.original['unitWorkloadId']}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick();
                }}
                className="underline underline-offset-2 hover:text-deep-blue hover:decoration-deep-blue"
              >
                {row.original['udc']}
              </a>
            </span>
            <br />
            <span className="text-xs font-normal text-primary-black-90">{row.original['unitName']}</span>
          </>
        );
        break;

      case 'academics':
        if (row.original['academics'].length > 0) {
          value = (
            <span>
              {row.original['academics']
                .map(
                  (academic: { firstName: string; lastName: string }) => `${academic.lastName}, ${academic.firstName}`,
                )
                .join('\u00A0\u00A0\u00A0\u00A0\u00A0')}
            </span>
          );
        } else {
          value = (
            <Button variant="pill" size="rounded-sm" className="h-6" onClick={handleClick} aria-label="Assign staff">
              Assign staff
            </Button>
          );
        }
        break;

      case 'tdPercentage':
      case 'trdPercentage':
        value = row.original[key] + '% allocated';
        break;

      default:
        value = row.original[key] ?? '-';
    }

    const combinedClassName = cn(
      'text-left text-b3 font-semibold py-[10px] py-3 text-secondary-grey-01 flex text-b3',
      className,
    );
    return (
      <div key={row.index} className={combinedClassName}>
        <div className="my-auto whitespace-pre pr-8 font-proxima">
          {key == 'unitName' ? (value ?? '-') : <span className="text-b3 text-secondary-grey-01">{value}</span>}
        </div>
      </div>
    );
  };

// columnId is needed in filterFn column option (https://tanstack.com/table/v8/docs/guide/column-filtering#filterfns)
const unitFilterFn: FilterFn<MappedSchoolUnitDetails> = (row, columnId, value) => {
  const unit = `${row.original.unitName} ${row.original.udc}`.toLowerCase();
  return unit.includes(value.toLowerCase());
};

export const schoolUnitColumns: ColumnDef<MappedSchoolUnitDetails>[] = [
  {
    id: 'unitId',
    accessorFn: (row) => `${row.udc} ${row.unitName} ${row.unitId}`,
    filterFn: unitFilterFn,
    sortingFn: 'text',
    header: createHeader('Unit', true),
    cell: createCell('unit'),
  },
  {
    accessorKey: 'studyPeriodName',
    header: createHeader('Study period', false),
    cell: createCell('studyPeriodName'),
  },
  {
    accessorKey: 'coordinator',
    header: createHeader('Coordinator', false),
    cell: createCell('coordinator'),
  },
  {
    accessorKey: 'tdPercentage',
    header: createHeader('TD', false),
    cell: createCell('tdPercentage'),
  },
  {
    accessorKey: 'trdPercentage',
    header: createHeader('TRD', false),
    cell: createCell('trdPercentage'),
  },
  {
    accessorKey: 'academics',
    header: createHeader('Staff', false),
    cell: createCell('academics'),
  },
];
